import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../../services/loggingService';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) return idToken;
    console.warn('Business API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Business API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Formats a Date as YYYY-MM-DD.
 * @param {Date} date
 * @returns {string}
 */
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Fetches dish dataset for a site within a date range.
 * @param {Object} params
 * @param {string} params.siteId - Site ID.
 * @param {Date} params.startDate - Start date (inclusive).
 * @param {Date} params.endDate - End date (inclusive).
 * @returns {Promise<Array<Object>>} List of dish dataset records.
 * @throws {Error} If auth token is missing or the API request fails.
 */
export async function fetchDishDataset({ siteId, startDate, endDate }) {
  const idToken = await getAuthToken();
  if (!idToken) {
    throw new Error('Authentication token not found');
  }

  const start = formatDate(startDate);
  const end = formatDate(endDate);
  const url = `${API_BASE_URL}/restaurant/dish-dataset?start_date=${start}&end_date=${end}&site_id=${encodeURIComponent(siteId)}`;

  let response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (fetchError) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_restaurant_site_analysis',
      `Restaurant Site Analysis API network/fetch error for fetchDishDataset`,
      fetchError,
      {
        endpoint: '/restaurant/dish-dataset',
        method: 'GET',
        siteId,
        startDate: start,
        endDate: end,
        errorType: fetchError?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[RestorantSiteApi] Failed to log error:', logError);
    });
    
    throw fetchError;
  }

  if (response.status === 200) {
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  const body = await response.text();
  const error = new Error(
    `Failed to fetch dish dataset: ${response.status} - ${body}`
  );
  error.status = response.status;
  error.statusText = response.statusText;
  error.endpoint = '/restaurant/dish-dataset';
  error.responseData = body;
  
  // Log API error to logging service
  loggingService.logError(
    'api_error_restaurant_site_analysis',
    `Restaurant Site Analysis API error: ${response.status} ${response.statusText} for fetchDishDataset`,
    error,
    {
      endpoint: '/restaurant/dish-dataset',
      method: 'GET',
      siteId,
      startDate: start,
      endDate: end,
      status: response.status,
      statusText: response.statusText,
      responseData: body,
    }
  ).catch(logError => {
    console.warn('[RestorantSiteApi] Failed to log error:', logError);
  });
  
  throw error;
}
